import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useActiveWorld } from '../hooks/useActiveWorld';

const OPERATORS = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'not equals' },
  { value: 'in', label: 'is one of' },
  { value: 'not_in', label: 'is not one of' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'exists', label: 'is not empty' },
  { value: 'not_exists', label: 'is empty' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'matches', label: 'matches regex' },
];

const ACTION_TYPES = [
  { value: 'set_tag', label: 'Add Tag', needsValue: true },
  { value: 'remove_tag', label: 'Remove Tag', needsValue: true },
  { value: 'set_field', label: 'Set Field', needsField: true, needsValue: true },
  { value: 'increment_field', label: 'Increment Field', needsField: true, needsValue: true },
  { value: 'set_priority', label: 'Set Priority', needsValue: true },
  { value: 'set_route', label: 'Set Route', needsValue: true },
  { value: 'set_status', label: 'Set Status', needsValue: true },
  { value: 'skip', label: 'Skip Contact', needsValue: false },
  { value: 'flag_review', label: 'Send to Review', needsValue: false },
];

const COMMON_FIELDS = [
  'lead_email', 'business_name', 'lead_title', 'lead_city', 'lead_country',
  'lead_website', 'lead_telephone', 'lead_description', 'lead_category',
  'lead_linkedin', 'lead_instagram', 'import_name', 'import_source',
  'lead_company_size', 'custom_source', 'custom_seniority', 'custom_role_category',
];

const EMPTY_RULE = {
  name: '',
  enabled: true,
  priority: 100,
  match: 'all',
  conditions: [{ field: '', operator: 'contains', value: '' }],
  actions: [{ type: 'set_tag', value: '' }],
};

const TEMPLATE_RULES = [
  {
    name: 'Tag hiring-sales',
    match: 'any',
    conditions: [
      { field: 'lead_title', operator: 'in', value: ['SDR', 'Head of Sales', 'Sales Manager', 'BDR', 'Account Executive'] },
    ],
    actions: [
      { type: 'set_tag', value: 'hiring-sales' },
      { type: 'increment_field', field: 'lead_priority', value: '10' },
    ],
  },
  {
    name: 'Enterprise route',
    match: 'all',
    conditions: [
      { field: 'lead_company_size', operator: 'gt', value: '50' },
    ],
    actions: [
      { type: 'set_route', value: 'enterprise' },
      { type: 'set_tag', value: 'enterprise' },
    ],
  },
  {
    name: 'Skip empty emails',
    match: 'all',
    conditions: [
      { field: 'lead_email', operator: 'not_exists', value: '' },
      { field: 'lead_website', operator: 'not_exists', value: '' },
    ],
    actions: [
      { type: 'skip', value: 'No email and no website' },
    ],
  },
];

const ImportRuleEditor = ({ onClose }) => {
  // 🔒 TEAM SUPPORT: operate against the active workspace owner so team
  // members edit the owner's import rules instead of their own.
  const { userId } = useActiveWorld();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);

  const functions = getFunctions(undefined, 'europe-west1');

  const loadRules = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const getRules = httpsCallable(functions, 'getImportRules');
      const result = await getRules({ userId });
      if (result.data.success) {
        setRules(result.data.rules || []);
      }
    } catch (err) {
      console.error('Error loading rules:', err);
      setError('Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, [functions, userId]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const saveRule = async (rule) => {
    setSaving(true);
    setError(null);
    try {
      // Convert 'in' / 'not_in' values from comma-separated string to array
      const processedRule = {
        ...rule,
        conditions: rule.conditions.map(c => {
          if ((c.operator === 'in' || c.operator === 'not_in') && typeof c.value === 'string') {
            return { ...c, value: c.value.split(',').map(v => v.trim()).filter(Boolean) };
          }
          return c;
        }),
      };

      const saveFn = httpsCallable(functions, 'saveImportRule');
      const result = await saveFn({ userId, rule: processedRule });

      if (result.data.success) {
        await loadRules();
        setEditingRule(null);
      }
    } catch (err) {
      console.error('Error saving rule:', err);
      setError(err.message || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      const deleteFn = httpsCallable(functions, 'deleteImportRule');
      await deleteFn({ userId, ruleId });
      setRules(prev => prev.filter(r => r.id !== ruleId));
    } catch (err) {
      console.error('Error deleting rule:', err);
      setError('Failed to delete rule');
    }
  };

  const addCondition = () => {
    if (!editingRule) return;
    setEditingRule(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: '', operator: 'contains', value: '' }],
    }));
  };

  const removeCondition = (index) => {
    if (!editingRule || editingRule.conditions.length <= 1) return;
    setEditingRule(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  const updateCondition = (index, updates) => {
    setEditingRule(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => i === index ? { ...c, ...updates } : c),
    }));
  };

  const addAction = () => {
    if (!editingRule) return;
    setEditingRule(prev => ({
      ...prev,
      actions: [...prev.actions, { type: 'set_tag', value: '' }],
    }));
  };

  const removeAction = (index) => {
    if (!editingRule || editingRule.actions.length <= 1) return;
    setEditingRule(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index),
    }));
  };

  const updateAction = (index, updates) => {
    setEditingRule(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) => i === index ? { ...a, ...updates } : a),
    }));
  };

  const loadTemplate = (template) => {
    setEditingRule({
      ...EMPTY_RULE,
      ...template,
      enabled: true,
      priority: 100,
    });
    setShowTemplates(false);
  };

  return (
    <div className="smart-review-overlay" onClick={onClose}>
      <div className="rule-editor-modal" onClick={e => e.stopPropagation()}>
        <div className="rule-editor-header">
          <h2>Import Rules</h2>
          <p>Define if/then rules to auto-process incoming contacts</p>
          <button className="smart-review-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {error && <div className="smart-review-error">{error}</div>}

        <div className="rule-editor-body">
          {/* Rule Editing Form */}
          {editingRule ? (
            <div className="rule-edit-form">
              <div className="rule-edit-top">
                <input
                  type="text"
                  className="rule-name-input"
                  placeholder="Rule name (e.g., Tag hiring-sales)"
                  value={editingRule.name}
                  onChange={e => setEditingRule(prev => ({ ...prev, name: e.target.value }))}
                />
                <div className="rule-match-toggle">
                  <span>Match</span>
                  <button
                    className={`rule-match-btn ${editingRule.match === 'all' ? 'active' : ''}`}
                    onClick={() => setEditingRule(prev => ({ ...prev, match: 'all' }))}
                  >
                    ALL
                  </button>
                  <button
                    className={`rule-match-btn ${editingRule.match === 'any' ? 'active' : ''}`}
                    onClick={() => setEditingRule(prev => ({ ...prev, match: 'any' }))}
                  >
                    ANY
                  </button>
                </div>
              </div>

              {/* Conditions */}
              <div className="rule-section">
                <h4>IF</h4>
                {editingRule.conditions.map((condition, i) => (
                  <div key={i} className="rule-condition-row">
                    <select
                      value={condition.field}
                      onChange={e => updateCondition(i, { field: e.target.value })}
                      className="rule-field-select"
                    >
                      <option value="">Select field...</option>
                      {COMMON_FIELDS.map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                    <select
                      value={condition.operator}
                      onChange={e => updateCondition(i, { operator: e.target.value })}
                      className="rule-operator-select"
                    >
                      {OPERATORS.map(op => (
                        <option key={op.value} value={op.value}>{op.label}</option>
                      ))}
                    </select>
                    {!['exists', 'not_exists'].includes(condition.operator) && (
                      <input
                        type="text"
                        value={Array.isArray(condition.value) ? condition.value.join(', ') : (condition.value || '')}
                        onChange={e => updateCondition(i, { value: e.target.value })}
                        placeholder={['in', 'not_in'].includes(condition.operator) ? 'value1, value2, ...' : 'value'}
                        className="rule-value-input"
                      />
                    )}
                    {editingRule.conditions.length > 1 && (
                      <button className="rule-remove-btn" onClick={() => removeCondition(i)}>×</button>
                    )}
                  </div>
                ))}
                <button className="rule-add-btn" onClick={addCondition}>+ Add Condition</button>
              </div>

              {/* Actions */}
              <div className="rule-section">
                <h4>THEN</h4>
                {editingRule.actions.map((action, i) => {
                  const actionDef = ACTION_TYPES.find(a => a.value === action.type);
                  return (
                    <div key={i} className="rule-action-row">
                      <select
                        value={action.type}
                        onChange={e => updateAction(i, { type: e.target.value })}
                        className="rule-action-select"
                      >
                        {ACTION_TYPES.map(a => (
                          <option key={a.value} value={a.value}>{a.label}</option>
                        ))}
                      </select>
                      {actionDef?.needsField && (
                        <select
                          value={action.field || ''}
                          onChange={e => updateAction(i, { field: e.target.value })}
                          className="rule-field-select"
                        >
                          <option value="">Select field...</option>
                          {COMMON_FIELDS.map(f => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                      )}
                      {actionDef?.needsValue !== false && (
                        <input
                          type="text"
                          value={action.value || ''}
                          onChange={e => updateAction(i, { value: e.target.value })}
                          placeholder="value"
                          className="rule-value-input"
                        />
                      )}
                      {editingRule.actions.length > 1 && (
                        <button className="rule-remove-btn" onClick={() => removeAction(i)}>×</button>
                      )}
                    </div>
                  );
                })}
                <button className="rule-add-btn" onClick={addAction}>+ Add Action</button>
              </div>

              {/* Priority */}
              <div className="rule-priority-row">
                <label>Priority (lower runs first)</label>
                <input
                  type="number"
                  value={editingRule.priority}
                  onChange={e => setEditingRule(prev => ({ ...prev, priority: parseInt(e.target.value) || 100 }))}
                  className="rule-priority-input"
                  min="1"
                  max="1000"
                />
              </div>

              <div className="rule-edit-actions">
                <button
                  className="rule-save-btn"
                  onClick={() => saveRule(editingRule)}
                  disabled={saving || !editingRule.name.trim()}
                >
                  {saving ? 'Saving...' : editingRule.id ? 'Update Rule' : 'Create Rule'}
                </button>
                <button
                  className="rule-cancel-btn"
                  onClick={() => setEditingRule(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Actions Bar */}
              <div className="rule-toolbar">
                <button
                  className="rule-new-btn"
                  onClick={() => setEditingRule({ ...EMPTY_RULE })}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  New Rule
                </button>
                <button
                  className="rule-template-btn"
                  onClick={() => setShowTemplates(!showTemplates)}
                >
                  Templates
                </button>
              </div>

              {/* Templates */}
              {showTemplates && (
                <div className="rule-templates">
                  {TEMPLATE_RULES.map((t, i) => (
                    <div key={i} className="rule-template-card" onClick={() => loadTemplate(t)}>
                      <h5>{t.name}</h5>
                      <p>
                        IF {t.conditions.map(c => `${c.field} ${c.operator} ${Array.isArray(c.value) ? c.value.join(', ') : c.value}`).join(' AND ')}
                      </p>
                      <p>
                        THEN {t.actions.map(a => `${a.type}${a.value ? `: ${a.value}` : ''}`).join(', ')}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Rules List */}
              {loading ? (
                <div className="smart-review-loading">
                  <div className="api-settings-spinner" />
                  <p>Loading rules...</p>
                </div>
              ) : rules.length === 0 ? (
                <div className="smart-review-empty">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                    <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>No Rules Yet</h3>
                  <p>Create rules to auto-tag, prioritize, or route contacts during smart import.</p>
                </div>
              ) : (
                <div className="rule-list">
                  {rules.map(rule => (
                    <div key={rule.id} className={`rule-list-item ${rule.enabled === false ? 'disabled' : ''}`}>
                      <div className="rule-list-item-header">
                        <div className={`rule-enabled-dot ${rule.enabled !== false ? 'on' : 'off'}`} />
                        <span className="rule-list-name">{rule.name}</span>
                        <span className="rule-list-priority">Priority: {rule.priority || 100}</span>
                      </div>
                      <div className="rule-list-summary">
                        <span className="rule-summary-if">
                          IF ({rule.match || 'all'}) {(rule.conditions || []).map(c =>
                            `${c.field} ${c.operator}${c.value ? ` "${Array.isArray(c.value) ? c.value.join(', ') : c.value}"` : ''}`
                          ).join(` ${rule.match === 'any' ? 'OR' : 'AND'} `)}
                        </span>
                        <span className="rule-summary-then">
                          THEN {(rule.actions || []).map(a => {
                            const def = ACTION_TYPES.find(at => at.value === a.type);
                            return `${def?.label || a.type}${a.value ? `: ${a.value}` : ''}`;
                          }).join(', ')}
                        </span>
                      </div>
                      <div className="rule-list-actions">
                        <button onClick={() => setEditingRule({ ...rule })} className="rule-list-edit">Edit</button>
                        <button onClick={() => deleteRule(rule.id)} className="rule-list-delete">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportRuleEditor;
